import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Box,
  Avatar,
  Chip,
  Divider,
  CircularProgress,
  Button,
  Stack,
  Tooltip,
  alpha,
  useTheme,
  Grid,
  Tabs,
  Tab,
  Paper,
} from "@mui/material";
import {
  Close as CloseIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  LocationOn as LocationIcon,
  BusinessCenter as BusinessIcon,
  DateRange as DateIcon,
  AssignmentInd as AssignmentIcon,
  OpenInNew as OpenInNewIcon,
  PersonOutlined,
  BadgeOutlined,
  UpdateOutlined,
  SchoolOutlined,
  AccountBalanceWalletOutlined,
  PeopleAltOutlined,
  ReceiptLongOutlined,
  CommentOutlined,
  WcOutlined,
  HomeOutlined,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import moment from "moment";
import { capitalizeWords } from "functions";
import useAxiosPrivate from "app/hooks/useAxiosPrivate";

export default function ProspectDetailsDialog({
  open,
  onClose,
  prospectId,
  initialData = null,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const axiosPrivate = useAxiosPrivate();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(initialData);
  const [activeTab, setActiveTab] = useState(0); // 0 = Short Profile, 1 = Full Profile
  const [discussionCount, setDiscussionCount] = useState(0);
  const [familyCount, setFamilyCount] = useState(0);
  const [policiesCount, setPoliciesCount] = useState(0);

  useEffect(() => {
    let isMounted = true;
    if (open && prospectId) {
      setLoading(true);
      setData(initialData || null);
      setActiveTab(0);

      // Attempt to load full prospect details
      axiosPrivate
        .post("GetProspectByID.php", { UID: prospectId })
        .then((res) => {
          if (!isMounted) return;
          if (res.data?.status === 1 && res.data?.Data?.[0]) {
            setData(res.data.Data[0]);
            setDiscussionCount(
              Array.isArray(res.data.Discussion)
                ? res.data.Discussion.length
                : Number(res.data.Discussion) || 0
            );
            setFamilyCount(
              Array.isArray(res.data.Family)
                ? res.data.Family.length
                : Number(res.data.Family) || 0
            );
            setPoliciesCount(
              Array.isArray(res.data.Policy)
                ? res.data.Policy.length
                : Number(res.data.Policy) || 0
            );
          } else {
            // Fallback to GetReferenceByIDNew
            return axiosPrivate.post("GetReferenceByIDNew.php", { UID: prospectId });
          }
        })
        .then((fallbackRes) => {
          if (!isMounted || !fallbackRes) return;
          if (fallbackRes.data?.status === 1 && fallbackRes.data?.Data?.[0]) {
            setData(fallbackRes.data.Data[0]);
          }
        })
        .catch((err) => {
          console.error("Error loading prospect details:", err);
        })
        .finally(() => {
          if (isMounted) setLoading(false);
        });
    }

    return () => {
      isMounted = false;
    };
  }, [open, prospectId, initialData, axiosPrivate]);

  if (!open) return null;

  const current = data || initialData || {};
  const prospectName = capitalizeWords(current.Name || "Prospect Details");
  const phone = current.MobileNo || current.WhatsAppNo || current.Mobile || "";
  const wa = current.WhatsAppNo || current.MobileNo || current.Mobile || "";
  const fullPageUrl = `/Prospects/ViewProspect/${prospectId}`;

  const pictureUrl = current.Picture
    ? current.Picture.startsWith("http")
      ? current.Picture
      : `https://backend.dahiyainsurance.com/Upload/profile/${current.Picture}`
    : undefined;

  const handleCall = () => {
    if (phone) window.open(`tel:${phone}`, "_self");
  };

  const handleWhatsApp = () => {
    if (!wa) return;
    const cleanNumber = wa.replace(/\D/g, "");
    const formatted = cleanNumber.length === 10 ? `91${cleanNumber}` : cleanNumber;
    window.open(`https://wa.me/${formatted}`, "_blank");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3.5,
          background: isDark
            ? alpha(theme.palette.background.paper, 0.95)
            : alpha("#ffffff", 0.98),
          backdropFilter: "blur(20px)",
          boxShadow: isDark
            ? "0 24px 60px rgba(0,0,0,0.6)"
            : "0 20px 50px rgba(0,0,0,0.14)",
          border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
          overflow: "hidden",
        },
      }}
    >
      {/* ── Dialog Header ── */}
      <DialogTitle
        sx={{
          p: { xs: 2, sm: 2.5 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          background: isDark
            ? alpha(theme.palette.primary.main, 0.06)
            : alpha(theme.palette.primary.main, 0.03),
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
          <Avatar
            src={pictureUrl}
            sx={{
              width: 46,
              height: 46,
              fontWeight: 800,
              fontSize: "1.1rem",
              bgcolor: alpha(theme.palette.primary.main, 0.15),
              color: "primary.main",
              border: `2px solid ${alpha(theme.palette.primary.main, 0.3)}`,
            }}
          >
            {prospectName[0]?.toUpperCase() || <PersonOutlined />}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <Typography variant="h6" fontWeight={800} noWrap sx={{ letterSpacing: "-0.01em" }}>
                {prospectName}
              </Typography>
              {current.Status && (
                <Chip
                  label={current.Status}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: "0.68rem",
                    fontWeight: 800,
                    bgcolor:
                      current.Status === "HOT"
                        ? alpha(theme.palette.error.main, 0.15)
                        : alpha(theme.palette.primary.main, 0.12),
                    color:
                      current.Status === "HOT"
                        ? theme.palette.error.main
                        : theme.palette.primary.main,
                  }}
                />
              )}
              {current.SrNo && (
                <Chip
                  label={`ID: ${current.SrNo}`}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: "0.68rem", fontWeight: 700 }}
                />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {current.Village ? `${current.Village} • ` : ""}
              {current.Category || "Prospect Profile"}
            </Typography>
          </Box>
        </Box>

        {/* Header Right Actions */}
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Open Full Prospect Page">
            <Button
              component={Link}
              to={fullPageUrl}
              target="_blank"
              variant="contained"
              size="small"
              endIcon={<OpenInNewIcon sx={{ fontSize: "14px !important" }} />}
              sx={{
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                fontSize: "0.78rem",
                px: 1.75,
                boxShadow: "none",
              }}
            >
              Full Page
            </Button>
          </Tooltip>

          <IconButton onClick={onClose} size="small" sx={{ color: "text.secondary" }}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      {/* ── Mode Tabs: Short Profile vs Full Profile ── */}
      <Box sx={{ px: { xs: 2, sm: 2.5 }, pt: 0.5, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}` }}>
        <Tabs
          value={activeTab}
          onChange={(_, val) => setActiveTab(val)}
          sx={{
            minHeight: 42,
            "& .MuiTab-root": {
              minHeight: 42,
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.82rem",
              py: 0.5,
              px: 2,
            },
          }}
        >
          <Tab icon={<PersonOutlined sx={{ fontSize: 18 }} />} iconPosition="start" label="Short Profile" />
          <Tab icon={<BadgeOutlined sx={{ fontSize: 18 }} />} iconPosition="start" label="Full Profile" />
        </Tabs>
      </Box>

      {/* ── Dialog Content ── */}
      <DialogContent sx={{ p: { xs: 2, sm: 2.5 }, minHeight: 280 }}>
        {loading && !data ? (
          <Box sx={{ py: 6, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
            <CircularProgress size={36} />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, fontWeight: 600 }}>
              Loading prospect profile…
            </Typography>
          </Box>
        ) : (
          <Box>
            {/* ════════════════════════════════════════════════════════════════
               TAB 0: SHORT PROFILE (Quick snapshot + contacts + highlights)
               ════════════════════════════════════════════════════════════════ */}
            {activeTab === 0 && (
              <Stack spacing={2.5}>
                {/* Quick Action Contact Bar */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    borderRadius: 2.5,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    bgcolor: isDark ? alpha(theme.palette.background.default, 0.6) : "#f8fafc",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 1.5,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                    {phone && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <PhoneIcon sx={{ fontSize: 16, color: "primary.main" }} />
                        <Typography variant="body2" fontWeight={700}>
                          {phone}
                        </Typography>
                      </Box>
                    )}
                    {current.Village && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <LocationIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                        <Typography variant="body2" color="text.secondary">
                          {[current.Village, current.Tehsil, current.District].filter(Boolean).join(", ")}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Stack direction="row" spacing={1}>
                    {phone && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PhoneIcon />}
                        onClick={handleCall}
                        sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700, fontSize: "0.75rem" }}
                      >
                        Call
                      </Button>
                    )}
                    {wa && (
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<WhatsAppIcon />}
                        onClick={handleWhatsApp}
                        sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700, fontSize: "0.75rem" }}
                      >
                        WhatsApp
                      </Button>
                    )}
                  </Stack>
                </Paper>

                {/* Grid of Key Info */}
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2.5,
                        border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                        bgcolor: alpha(theme.palette.background.paper, 0.5),
                        height: "100%",
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={800} color="primary.main" sx={{ mb: 1.5 }}>
                        Personal Demographics
                      </Typography>
                      <Stack spacing={1.2}>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="caption" color="text.secondary">Name:</Typography>
                          <Typography variant="body2" fontWeight={600}>{current.Salutation ? `${current.Salutation} ` : ""}{prospectName}</Typography>
                        </Box>
                        {current.DOB && current.DOB !== "0000-00-00" && (
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography variant="caption" color="text.secondary">Date of Birth:</Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {moment(current.DOB).format("DD MMM YYYY")}
                              {current.Age ? ` (${current.Age} yrs)` : ""}
                            </Typography>
                          </Box>
                        )}
                        {current.Caste && (
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography variant="caption" color="text.secondary">Caste / Category:</Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {[current.Caste, current.CasteCategory].filter(Boolean).join(" • ")}
                            </Typography>
                          </Box>
                        )}
                        {current.Profession && (
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography variant="caption" color="text.secondary">Profession:</Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {current.Profession} {current.WorkType ? `(${current.WorkType})` : ""}
                            </Typography>
                          </Box>
                        )}
                        {current.Gender && (
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography variant="caption" color="text.secondary">Gender:</Typography>
                            <Typography variant="body2" fontWeight={600}>{current.Gender}</Typography>
                          </Box>
                        )}
                      </Stack>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2.5,
                        border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                        bgcolor: alpha(theme.palette.background.paper, 0.5),
                        height: "100%",
                      }}
                    >
                      <Typography variant="subtitle2" fontWeight={800} color="primary.main" sx={{ mb: 1.5 }}>
                        Prospect Coordinates
                      </Typography>
                      <Stack spacing={1.2}>
                        {current.RefBy && (
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography variant="caption" color="text.secondary">Referred By:</Typography>
                            <Typography variant="body2" fontWeight={600}>{current.RefByName || current.RefBy}</Typography>
                          </Box>
                        )}
                        {current.AssignedToName && (
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography variant="caption" color="text.secondary">Assigned To:</Typography>
                            <Typography variant="body2" fontWeight={600}>{current.AssignedToName}</Typography>
                          </Box>
                        )}
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography variant="caption" color="text.secondary">Location:</Typography>
                          <Typography variant="body2" fontWeight={600} align="right">
                            {[current.Village, current.District, current.State].filter(Boolean).join(", ") || "Not set"}
                          </Typography>
                        </Box>
                        {current.ModifiedOn && (
                          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                            <Typography variant="caption" color="text.secondary">Last Updated:</Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {current.ModifiedOn} {current.ModifiedBy ? `by ${current.ModifiedBy}` : ""}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </Paper>
                  </Grid>
                </Grid>

                {/* Recent Discussion Summary (if available) */}
                {current.ConceptDiscussed && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2.5,
                      border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                      bgcolor: alpha(theme.palette.primary.main, 0.03),
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                      <CommentOutlined sx={{ fontSize: 16, color: "primary.main" }} />
                      <Typography variant="caption" fontWeight={700} color="primary.main">
                        Concept / Last Discussed:
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontStyle: "italic", color: "text.primary" }}>
                      &ldquo;{current.ConceptDiscussed}&rdquo;
                    </Typography>
                  </Paper>
                )}
              </Stack>
            )}

            {/* ════════════════════════════════════════════════════════════════
               TAB 1: FULL PROFILE (Comprehensive prospect information)
               ════════════════════════════════════════════════════════════════ */}
            {activeTab === 1 && (
              <Stack spacing={2.5}>
                {/* Stats row */}
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        textAlign: "center",
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                      }}
                    >
                      <PeopleAltOutlined sx={{ fontSize: 20, color: "primary.main" }} />
                      <Typography variant="h6" fontWeight={800} color="primary.main">
                        {familyCount}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Family Members
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={4}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        textAlign: "center",
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.success.main, 0.05),
                        border: `1px solid ${alpha(theme.palette.success.main, 0.15)}`,
                      }}
                    >
                      <ReceiptLongOutlined sx={{ fontSize: 20, color: "success.main" }} />
                      <Typography variant="h6" fontWeight={800} color="success.main">
                        {policiesCount}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Active Policies
                      </Typography>
                    </Paper>
                  </Grid>

                  <Grid item xs={4}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        textAlign: "center",
                        borderRadius: 2,
                        bgcolor: alpha(theme.palette.warning.main, 0.05),
                        border: `1px solid ${alpha(theme.palette.warning.main, 0.15)}`,
                      }}
                    >
                      <CommentOutlined sx={{ fontSize: 20, color: "warning.main" }} />
                      <Typography variant="h6" fontWeight={800} color="warning.main">
                        {discussionCount}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        Discussions
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {/* Section A: Full Personal & Contact Coordinates */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2.5,
                    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.5 }}>
                    Personal & Contact Information
                  </Typography>
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Email Address:</Typography>
                      <Typography variant="body2" fontWeight={600}>{current.Email || "Not provided"}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Marital Status:</Typography>
                      <Typography variant="body2" fontWeight={600}>{current.MaritalStatus || "Not set"}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Education:</Typography>
                      <Typography variant="body2" fontWeight={600}>{current.Education || "Not set"}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary">Occupation:</Typography>
                      <Typography variant="body2" fontWeight={600}>{current.Occupation || current.Profession || "Not set"}</Typography>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Section B: Residential & Permanent Addresses */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2.5,
                    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.5 }}>
                    Address & Geography
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>
                        Present / Local Address:
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {[current.Address, current.Village, current.Tehsil, current.District, current.State, current.PIN ? `PIN: ${current.PIN}` : ""]
                          .filter(Boolean)
                          .join(", ") || "No address on record"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>
                        Permanent Address:
                      </Typography>
                      <Typography variant="body2" sx={{ mt: 0.5 }}>
                        {[current.Address2, current.Village2, current.Tehsil2, current.District2, current.State2, current.PIN2 ? `PIN: ${current.PIN2}` : ""]
                          .filter(Boolean)
                          .join(", ") || "Same as present / Not provided"}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>

                {/* Section C: Financial & Liability Profile */}
                {(current.YearlyIncome || current.Investment || current.BankLiability || current.OtherLiability) && (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2.5,
                      border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                    }}
                  >
                    <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1.5 }}>
                      Financial & Liability Profile
                    </Typography>
                    <Grid container spacing={1.5}>
                      {current.YearlyIncome && (
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Yearly Income:</Typography>
                          <Typography variant="body2" fontWeight={700} color="success.main">
                            ₹{current.YearlyIncome}
                          </Typography>
                        </Grid>
                      )}
                      {current.Investment && (
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Investments:</Typography>
                          <Typography variant="body2" fontWeight={700}>
                            ₹{current.Investment}
                          </Typography>
                        </Grid>
                      )}
                      {current.BankLiability && (
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Bank Liability:</Typography>
                          <Typography variant="body2" fontWeight={700} color="error.main">
                            ₹{current.BankLiability}
                          </Typography>
                        </Grid>
                      )}
                      {current.OtherLiability && (
                        <Grid item xs={6} sm={3}>
                          <Typography variant="caption" color="text.secondary">Other Liability:</Typography>
                          <Typography variant="body2" fontWeight={700} color="error.main">
                            ₹{current.OtherLiability}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                )}

                {/* Call to action */}
                <Box sx={{ pt: 1, display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    component={Link}
                    to={fullPageUrl}
                    target="_blank"
                    variant="contained"
                    endIcon={<OpenInNewIcon />}
                    sx={{
                      borderRadius: 2.5,
                      textTransform: "none",
                      fontWeight: 700,
                      px: 3,
                    }}
                  >
                    Open Full Prospect Page & Edit
                  </Button>
                </Box>
              </Stack>
            )}
          </Box>
        )}
      </DialogContent>

      <Divider />

      {/* ── Dialog Actions Footer ── */}
      <DialogActions sx={{ px: 3, py: 1.75, justifyContent: "space-between" }}>
        <Typography variant="caption" color="text.secondary">
          {current.ModifiedOn ? `Updated ${current.ModifiedOn}` : "Prospect Profile Modal"}
        </Typography>
        <Button
          onClick={onClose}
          variant="outlined"
          size="small"
          sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700 }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

ProspectDetailsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  prospectId: PropTypes.string,
  initialData: PropTypes.object,
};
